import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";
import { useShopProducts } from "@/features/shop/useShopProducts";
import { localeOf, numberLocaleOf } from "@/features/shop/shop.catalog";
import { PackagesHero } from "./components/PackagesHero";
import { PackagesPanel } from "./components/PackagesPanel";
import { PackagesClosing } from "./components/PackagesClosing";
import { MembershipCard, isMembership } from "./components/MembershipCard";
import { allPackagesSorted, indexProducts } from "./packages.catalog";
import { usePackages } from "./usePackages";

/** El destino de la flecha de la portada. */
const PACKAGES_GRID_ID = "paquetes";

/**
 * La rejilla pública de paquetes — `/paquetes`.
 *
 * ── Qué hace esta pantalla, y qué no ─────────────────────────────────
 * Compone. Portada, pestañas, panel y cierre son componentes con su
 * propio archivo; aquí solo vive el ESTADO que dos de ellos comparten —la
 * categoría activa y la moneda— y el cableado de las dos peticiones.
 *
 * ── Las dos categorías no se mezclan, y es estructural ───────────────
 * El documento es explícito: los paquetes de negocio digital NO aparecen
 * en la sección de paquetes personales. Aquí eso no es un estilo ni un
 * orden de aparición: `packagesOfCategory` reparte antes de pintar y solo
 * una de las dos listas llega a la rejilla. El razonamiento de por qué
 * pestañas —y no dos secciones apiladas, ni esconder una tras un enlace—
 * está en `PackageCategoryTabs`.
 *
 * `personal` es la de partida: es la categoría a la que apunta el llamado
 * del carrito ("¿quieres hasta un 15% de descuento?"), o sea por donde
 * entra casi todo el mundo.
 *
 * ── Dos peticiones, y una es opcional ────────────────────────────────
 * Los paquetes (`usePackages`) son lo que esta pantalla necesita. El
 * catálogo (`useShopProducts`) es un extra: los paquetes guardan
 * `productId` y cantidad, sin nombres, así que el catálogo es lo único
 * que permite escribir "2× Colágeno marino" en vez de un identificador.
 * Si esa segunda petición falla, las tarjetas siguen enteras y solo
 * pierden los nombres — por eso su error NO apaga la pantalla.
 *
 * ── Y el backend de paquetes puede no existir ────────────────────────
 * Cuando esto se escribió, `backend/src/modules/` no tenía módulo de
 * paquetes: `/api/packages` responde 404. La pantalla lo aguanta con el
 * aviso y el botón de reintentar, con la portada, la barra y el pie en
 * pie, y con los dos llamados del final —que son estáticos— intactos.
 *
 * ── Deuda heredada, la misma que anotó franquicias ───────────────────
 * `ShopLayout`, `useShopCurrency` y `localeOf` son cromo del SITIO, no de
 * la tienda. Que vivan en `features/shop` obliga a esta pantalla a
 * importar de otra funcionalidad. Con `/franquicias` ya eran dos; con
 * `/paquetes` son tres, y toca subirlos a un sitio neutral.
 */
export function PackagesPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const numberLocale = numberLocaleOf(locale);

  const [currency, setCurrency] = useShopCurrency();

  const { data, isPending, isError, error, refetch, isFetching } = usePackages();
  const catalog = useShopProducts();

  const packages = useMemo(() => data?.items ?? [], [data]);

  /*
   * ── Una sola lista, como en producción (Anahí, 28-ago) ───────────
   * Antes había dos pestañas —recompra personal / negocio digital— y
   * los mayoristas no se veían al entrar. Ahora se enseñan TODOS en un
   * orden; cada tarjeta lleva su categoría como etiqueta, y qué es
   * mayorista se decide desde el panel de super-admin.
   */
  const visible = useMemo(() => allPackagesSorted(packages), [packages]);
  /* Las MEMBRESÍAS (productos tipo `membership`) van al frente, como en
     producción: 02 DUO · 01 Arranque · 03 Fundador. */
  const memberships = useMemo(
    () =>
      (catalog.data?.items ?? [])
        .filter(isMembership)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug)),
    [catalog.data],
  );

  /*
   * `null` mientras el catálogo viaja o si falla: `PackageContents` sabe
   * pintarse sin él. Un `Map` vacío mentiría —diría "ningún producto
   * coincide" cuando la verdad es "todavía no sé".
   */
  const productsById = useMemo(
    () => (catalog.data ? indexProducts(catalog.data.items) : null),
    [catalog.data],
  );

  return (
    /*
     * El carrito se monta AQUÍ y no en `app/providers.tsx`, igual que en
     * la tienda y en franquicias: `ShopLayout` es quien conecta el
     * contador de la barra superior con el carrito de verdad, y quien
     * llega a esta página con productos dentro sigue teniéndolos a mano.
     */
    <CartProvider>
      <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
        <PackagesHero gridHref={`#${PACKAGES_GRID_ID}`} />

        {/*
          `tabIndex={-1}` no lo mete en el orden de tabulación: está para
          poder MOVER el foco aquí al bajar desde la portada. `scroll-mt-24`
          reserva el alto de la barra superior, que es pegajosa: sin ese
          margen el desplazamiento deja las pestañas escondidas debajo.
        */}
        <section
          id={PACKAGES_GRID_ID}
          tabIndex={-1}
          className="scroll-mt-24 pb-16 pt-4 focus:outline-none sm:pb-20 sm:pt-6"
        >
          <div className="mx-auto grid max-w-6xl gap-8 px-6 sm:px-8">

            {memberships.length > 0 && (
              <ul
                aria-label={t("Membresías")}
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {memberships.map((product) => (
                  <li key={product.id}>
                    <MembershipCard
                      product={product}
                      currency={currency}
                      locale={locale}
                      numberLocale={numberLocale}
                    />
                  </li>
                ))}
              </ul>
            )}
            <PackagesPanel
              packages={visible}
              isPending={isPending}
              isError={isError}
              error={error}
              onRetry={() => void refetch()}
              retrying={isFetching}
              currency={currency}
              locale={locale}
              numberLocale={numberLocale}
              productsById={productsById}
            />

            <PackagesClosing />
          </div>
        </section>
      </ShopLayout>
    </CartProvider>
  );
}
